import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

export const copyTextToClipboard = async (textToCopy: string) => {
    // Navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(textToCopy);
            console.log('Text copied to clipboard using navigator.clipboard');
        } catch (error) {
            console.error('Failed to copy text using navigator.clipboard:', error);
        }
    } else {
        const isModalActive = () => {
            const modals = document.querySelectorAll('.v-overlay.v-overlay--active.v-dialog');
            for (const modal of modals) {
                if ((modal as HTMLElement).style.display !== 'none') {
                    return modal;
                }
            }
            return false;
        };

        // Check if a modal is active
        const modal = isModalActive();

        const textArea = document.createElement('textarea');

        // Use the 'out of viewport hidden text area' trick
        textArea.value = textToCopy;

        // Move the textarea out of the viewport, so it's not visible
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        textArea.style.top = '0';
        textArea.style.opacity = '0';
        textArea.classList.add('copy-textarea');


        if (modal) {
            const dialog = modal.querySelector('.v-overlay__content');
            if (dialog) {
                dialog.appendChild(textArea);
            } else {
                document.body.prepend(textArea);
            }
        } else {
            document.body.prepend(textArea);
        }

        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                console.log('Text copied to clipboard using document.execCommand');
            } else {
                console.error('Failed to copy text using document.execCommand');
            }
        } catch (error) {
            console.error('Failed to copy text using document.execCommand:', error);
        } finally {
            textArea.remove();
        }
    }
};


export const round = (value: number, decimals: number = 2) => {
    return parseFloat(value.toFixed(decimals));
}

export const formatNumberWithCommas = (x) => {
    if (x === null || isNaN(Number(x))) {
        return '0';
    }
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};


export const titleCase = (str) => {
    return str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const makeResizableDiv = (selector: string): void => {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) return;

    const el = element!;

    const resizers = document.querySelectorAll<HTMLElement>(`${selector} .resizer`);
    const minimum_size = 20;

    let original_width = 0;
    let original_height = 0;
    let original_x = 0;
    let original_y = 0;
    let original_mouse_x = 0;
    let original_mouse_y = 0;
    resizers.forEach((currentResizer) => {
        currentResizer.addEventListener('mousedown', (e: MouseEvent) => {
            e.preventDefault();
            original_width = parseFloat(getComputedStyle(el, null).getPropertyValue('width').replace('px', ''));
            original_height = parseFloat(getComputedStyle(el, null).getPropertyValue('height').replace('px', ''));
            original_x = el.getBoundingClientRect().left;
            original_y = el.getBoundingClientRect().top;
            original_mouse_x = e.pageX;
            original_mouse_y = e.pageY;

            function resize(e: MouseEvent) {
                const dx = e.pageX - original_mouse_x;
                const dy = e.pageY - original_mouse_y;

                const updateStyle = (width?: number, height?: number, left?: number, top?: number) => {
                    if (width && width > minimum_size) el.style.width = `${width}px`;
                    if (height && height > minimum_size) el.style.height = `${height}px`;
                    if (left !== undefined) el.style.left = `${left}px`;
                    if (top !== undefined) el.style.top = `${top}px`;
                };

                if (currentResizer.classList.contains('bottom-right')) {
                    updateStyle(original_width + dx, original_height + dy);
                } else if (currentResizer.classList.contains('bottom-left')) {
                    updateStyle(original_width - dx, original_height + dy, original_x + dx);
                } else if (currentResizer.classList.contains('top-right')) {
                    updateStyle(original_width + dx, original_height - dy, undefined, original_y + dy);
                } else if (currentResizer.classList.contains('top-left')) {
                    updateStyle(original_width - dx, original_height - dy, original_x + dx, original_y + dy);
                }
            }


            function stopResize() {
                window.removeEventListener('mousemove', resize);
                window.removeEventListener('mouseup', stopResize);
            }

            window.addEventListener('mousemove', resize);
            window.addEventListener('mouseup', stopResize);
        });


    });
};


export const adjustDurationForDaylightSavings = (dates) => {
    const getFirstSunday = (month, year) => {
        let date = dayjs(`${year}-${month + 1}-01`);
        while (date.day() !== 0) {
            date = date.add(1, 'day');
        }
        return date;
    };

    const dstStart = getFirstSunday(2, dayjs(dates.start).year()); // March
    const dstEnd = getFirstSunday(10, dayjs(dates.end).year()); // November

    if (dayjs(dates.start).isBefore(dstStart) && dayjs(dates.end).isAfter(dstStart)) {
        return 1;
    }
    if (dayjs(dates.start).isBefore(dstEnd) && dayjs(dates.end).isAfter(dstEnd)) {
        return -1;
    }

    return 0;
};

export const dragElement = (element: HTMLElement) => {
    let pos1 = 0;
    let pos2 = 0;
    let pos3 = 0;
    let pos4 = 0;

    const header = document.getElementById(`${element.id}header`);

    if (header) {
        header.onmousedown = dragMouseDown;
    } else {
        element.onmousedown = dragMouseDown;
    }

    function dragMouseDown(e) {
        e = e || window.event;
        e.preventDefault();
        // get the mouse cursor position at startup:
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        // call a function whenever the cursor moves:
        document.onmousemove = elementDrag;
    }

    function elementDrag(e) {
        e = e || window.event;
        e.preventDefault();
        // calculate the new cursor position:
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        // set the element's new position:
        element.style.top = `${element.offsetTop - pos2}px`;
        element.style.left = `${element.offsetLeft - pos1}px`;
    }

    function closeDragElement() {
        // stop moving when mouse button is released:
        document.onmouseup = null;
        document.onmousemove = null;
    }
};
